/**
 * 🔊 battleSfx.js — Web Audio synthesized battle sound effects.
 *
 * Zero external files. Everything is generated on the fly via AudioContext
 * oscillators + noise buffers. Lightweight, no CORS, no licensing.
 *
 * Usage:
 *   import { playSfx, setMuted, isMuted } from "@/utils/battleSfx";
 *   playSfx("hit_light");       // soft thud
 *   playSfx("hit_heavy");       // big thump
 *   playSfx("crit");            // crit chime
 *   playSfx("swing");           // attack whoosh
 *   playSfx("boss_roar");       // boss intro roar
 *   playSfx("ultimate");        // ultimate cast
 *   playSfx("ko");              // pet knocked out
 *   playSfx("stagger");         // stagger ping
 *   playSfx("victory");         // 3-note fanfare
 *   playSfx("defeat");          // descending sigh
 *
 * Note: AudioContext must be created/resumed after a user gesture in modern
 * browsers. We lazy-init on first call.
 */

let _ctx = null;
let _muted = (() => {
  try { return localStorage.getItem("omni_sfx_muted") === "1"; } catch { return false; }
})();

function getCtx() {
  if (_ctx) return _ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  } catch {
    _ctx = null;
  }
  return _ctx;
}

export function isMuted() { return _muted; }
export function setMuted(v) {
  _muted = !!v;
  try { localStorage.setItem("omni_sfx_muted", _muted ? "1" : "0"); } catch {}
}
export function toggleMuted() {
  setMuted(!_muted);
  return _muted;
}

// ─── Building blocks ────────────────────────────────────────────────
function envGain(ctx, dest, peak, attack, decay, t0) {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  g.connect(dest);
  return g;
}

function tone({ ctx, dest, type = "sine", from, to, peak, attack, decay, t0 }) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(from, t0);
  if (to !== undefined && to !== from) {
    o.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + attack + decay);
  }
  const g = envGain(ctx, dest, peak, attack, decay, t0);
  o.connect(g);
  o.start(t0);
  o.stop(t0 + attack + decay + 0.05);
}

function noiseBurst({ ctx, dest, duration, peak, t0, lowpass = 1200 }) {
  // Generate short white noise buffer
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * duration);
  const buf = ctx.createBuffer(1, len, sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = lowpass;
  src.connect(lp);
  const g = envGain(ctx, dest, peak, 0.005, duration, t0);
  lp.connect(g);
  src.start(t0);
  src.stop(t0 + duration + 0.05);
}

// ─── Patches ─────────────────────────────────────────────────────────
const PATCHES = {
  hit_light: (ctx, t) => {
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.08, peak: 0.25, t0: t, lowpass: 2200 });
    tone({ ctx, dest: ctx.destination, type: "sine", from: 220, to: 80, peak: 0.18, attack: 0.005, decay: 0.18, t0: t });
  },
  hit_heavy: (ctx, t) => {
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.18, peak: 0.45, t0: t, lowpass: 700 });
    tone({ ctx, dest: ctx.destination, type: "sine", from: 110, to: 38, peak: 0.55, attack: 0.005, decay: 0.32, t0: t });
    tone({ ctx, dest: ctx.destination, type: "triangle", from: 60, to: 22, peak: 0.25, attack: 0.005, decay: 0.34, t0: t });
  },
  crit: (ctx, t) => {
    // 3-tone metallic chime
    [880, 1320, 1760].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "triangle", from: f, peak: 0.18, attack: 0.005, decay: 0.42, t0: t + i * 0.025 });
    });
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.12, peak: 0.15, t0: t, lowpass: 4500 });
  },
  swing: (ctx, t) => {
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.14, peak: 0.22, t0: t, lowpass: 1800 });
  },
  defend: (ctx, t) => {
    tone({ ctx, dest: ctx.destination, type: "square", from: 440, to: 280, peak: 0.18, attack: 0.005, decay: 0.18, t0: t });
  },
  boss_roar: (ctx, t) => {
    // Detuned saws + noise
    [50, 75, 110].forEach((f) => {
      tone({ ctx, dest: ctx.destination, type: "sawtooth", from: f, to: f * 0.7, peak: 0.28, attack: 0.02, decay: 0.7, t0: t });
    });
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.7, peak: 0.18, t0: t, lowpass: 400 });
  },
  ultimate: (ctx, t) => {
    // Long ominous sweep up + boom
    tone({ ctx, dest: ctx.destination, type: "sawtooth", from: 70, to: 280, peak: 0.35, attack: 0.05, decay: 0.55, t0: t });
    tone({ ctx, dest: ctx.destination, type: "sine", from: 35, to: 140, peak: 0.4, attack: 0.05, decay: 0.6, t0: t });
    setTimeout(() => {
      const ctx2 = getCtx(); if (!ctx2) return;
      const tt = ctx2.currentTime;
      tone({ ctx: ctx2, dest: ctx2.destination, type: "sine", from: 80, to: 30, peak: 0.55, attack: 0.005, decay: 0.55, t0: tt });
      noiseBurst({ ctx: ctx2, dest: ctx2.destination, duration: 0.35, peak: 0.4, t0: tt, lowpass: 800 });
    }, 600);
  },
  ko: (ctx, t) => {
    // Descending wobble
    tone({ ctx, dest: ctx.destination, type: "sine", from: 440, to: 110, peak: 0.3, attack: 0.01, decay: 0.7, t0: t });
    tone({ ctx, dest: ctx.destination, type: "triangle", from: 220, to: 55, peak: 0.18, attack: 0.01, decay: 0.7, t0: t });
  },
  stagger: (ctx, t) => {
    // Wobbly dissonance
    [330, 349].forEach((f) => {
      tone({ ctx, dest: ctx.destination, type: "triangle", from: f, peak: 0.18, attack: 0.005, decay: 0.22, t0: t });
    });
  },
  victory: (ctx, t) => {
    // C-E-G fanfare
    [523.25, 659.25, 783.99].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "triangle", from: f, peak: 0.22, attack: 0.01, decay: 0.35, t0: t + i * 0.12 });
    });
  },
  defeat: (ctx, t) => {
    [392, 311, 220].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "sawtooth", from: f, peak: 0.22, attack: 0.01, decay: 0.45, t0: t + i * 0.18 });
    });
  },
  loot_drop: (ctx, t) => {
    [523, 784, 1046].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "sine", from: f, peak: 0.16, attack: 0.005, decay: 0.18, t0: t + i * 0.05 });
    });
  },
  // ─── 🎬 Cinematic SFX (Feb 2026) ───
  intro_gong: (ctx, t) => {
    // Deep gong + airy shimmer — "ROUND 1, FIGHT!" energy
    tone({ ctx, dest: ctx.destination, type: "sine", from: 70, to: 50, peak: 0.5, attack: 0.005, decay: 1.1, t0: t });
    tone({ ctx, dest: ctx.destination, type: "triangle", from: 140, to: 95, peak: 0.3, attack: 0.005, decay: 1.0, t0: t });
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.5, peak: 0.18, t0: t, lowpass: 1500 });
  },
  fight_call: (ctx, t) => {
    // Sharp upward "FIGHT!" — bright fifth + brassy noise
    [659, 880, 1175].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "square", from: f, peak: 0.18, attack: 0.005, decay: 0.32, t0: t + i * 0.04 });
    });
    noiseBurst({ ctx, dest: ctx.destination, duration: 0.15, peak: 0.18, t0: t, lowpass: 3500 });
  },
  ult_announce: (ctx, t) => {
    // Doom-bell + sustain — paired with ultimate cinema banner
    tone({ ctx, dest: ctx.destination, type: "sawtooth", from: 55, to: 30, peak: 0.4, attack: 0.005, decay: 1.4, t0: t });
    tone({ ctx, dest: ctx.destination, type: "triangle", from: 220, to: 110, peak: 0.32, attack: 0.005, decay: 1.2, t0: t });
    tone({ ctx, dest: ctx.destination, type: "sine", from: 880, to: 440, peak: 0.18, attack: 0.005, decay: 1.0, t0: t });
  },
  victory_grand: (ctx, t) => {
    // 5-note rising fanfare — C-E-G-C-G (octave climb)
    [523, 659, 784, 1046, 784].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "triangle", from: f, peak: 0.24, attack: 0.01, decay: 0.4, t0: t + i * 0.13 });
    });
    setTimeout(() => {
      const c2 = getCtx(); if (!c2) return;
      const tt = c2.currentTime;
      noiseBurst({ ctx: c2, dest: c2.destination, duration: 0.35, peak: 0.14, t0: tt, lowpass: 5000 });
    }, 650);
  },
  defeat_grand: (ctx, t) => {
    // 4-note descending dirge — slow + grave
    [466, 392, 311, 220].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "sawtooth", from: f, to: f * 0.92, peak: 0.24, attack: 0.01, decay: 0.55, t0: t + i * 0.22 });
    });
  },
  reward_chime: (ctx, t) => {
    // Bright triple-bell — for chips flying in
    [1046, 1318, 1568].forEach((f, i) => {
      tone({ ctx, dest: ctx.destination, type: "sine", from: f, peak: 0.18, attack: 0.005, decay: 0.28, t0: t + i * 0.06 });
    });
  },
};

export function playSfx(name) {
  if (_muted) return;
  const ctx = getCtx();
  if (!ctx) return;
  // Auto-resume if suspended (some browsers suspend on tab blur)
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  const patch = PATCHES[name];
  if (!patch) return;
  try {
    patch(ctx, ctx.currentTime + 0.005);
  } catch {
    // swallow — SFX should never crash gameplay
  }
}

/** Maps a battle log entry to the SFX name that should fire. */
export function sfxForLogEntry(entry) {
  if (!entry) return null;
  if (entry.type === "attack") {
    if (entry.crit) return "crit";
    if (entry.move === "heavy") return "hit_heavy";
    if (entry.move === "defend") return "defend";
    return "hit_light";
  }
  if (entry.type === "ultimate") return "ultimate";
  if (entry.type === "charging") return "boss_roar";
  if (entry.type === "ko") return "ko";
  if (entry.type === "stagger_apply") return "stagger";
  if (entry.type === "burn") return "hit_light";
  // 🌶️ Status Effect Engine V1 — DoT/HoT ticks and stun
  if (entry.type === "status_burn") return "hit_light";
  if (entry.type === "status_poison") return "hit_light";
  if (entry.type === "status_bleed") return "hit_light";
  if (entry.type === "status_regen") return "defend";
  if (entry.type === "status_stun_skip") return "stagger";
  return null;
}
